import DesignSystem from './System';
import colorPalette, { SystemColorPalette } from './System/tokens/colorPalette';

import {
  zIndex,
  border,
  fontWeight,
  lineHeight,
  breakpoints,
  sizes,
  grid,
  boxShadow,
  gradient,
  type SystemBreakpointMap,
  type SystemZIndexMap,
  type SystemSizeMap,
  type SystemBorder,
  type SystemLineHeightMap,
  type SystemFontWeightMap,
  type SystemGridMap,
  type SystemBoxShadowMap,
  type SystemGradientMap,
  type SystemLeadScoreMap,
  leadScore,
} from './System/tokens';
import type { ColorString } from './System/types';

export type {
  SizeMap,
  SystemSize,
  SystemSizeMap,
  SystemColorPalette,
  SystemFontWeight,
} from './System/tokens';

export type SystemType = {
  baseFontSize: string;
  fontFamily: { sans: string; monospace: string };
  fontWeight: SystemFontWeightMap;
  sizes: SystemSizeMap;
  lineHeight: SystemLineHeightMap;
};

export type SystemTokens = {
  type: SystemType;
  breakpoints: SystemBreakpointMap;
  colors: {
    colorPalette: SystemColorPalette;
    chart: Array<ColorString>;
    portals: Array<ColorString>;
    gradient: SystemGradientMap;
    leadScore: SystemLeadScoreMap;
    ai: {
      primary: ColorString;
      glowingGradient: string;
    };
  };
  zIndex: SystemZIndexMap;
  spacing: { scale: SystemSizeMap };
  border: SystemBorder;
  grid: SystemGridMap;
  boxShadow: SystemBoxShadowMap;
};

const tokens: SystemTokens = {
  type: {
    baseFontSize: '14px',
    sizes,
    fontFamily: {
      sans: '"Wotfard","Helvetica Neue","Helvetica","Arial",sans-serif',
      monospace: '"Fira Code", monospace',
    },
    fontWeight,
    lineHeight,
  },
  colors: {
    colorPalette,
    chart: [
      '#1E2647',
      '#3763D4',
      '#B6D3FF',
      '#EAEFF5',
      '#FFD6A8',
      '#FFB464',
      '#FF6900',
      '#A2040E',
      '#642901',
    ],
    portals: [
      '#2346A8', // deep blue
      '#7AA6FF', // light blue
      '#D7E6FF', // very light blue

      '#FF7A1A', // bright orange
      '#FFC07A', // peach
      '#FFE2C2', // pale peach

      '#6A5CFF', // indigo
      '#8B7BFF', // soft purple

      '#A2040E', // deep red
      '#C44300', // burnt orange-red
      '#7A3B00', // brown
      '#3E2A1A', // dark brown
      '#1E2647', // deep navy
    ],
    gradient,
    leadScore,
    ai: {
      primary: '#7a00ff',
      glowingGradient: `
        45deg,
        #c95a89,
        #fffb00,
        #48ff00,
        #00ffd5,
        #f538d5,
        #7a00ff,
        #cc00ff,
        #ff0000
      `,
    },
  },
  spacing: {
    scale: sizes,
  },
  border,
  zIndex,
  breakpoints,
  grid,
  boxShadow,
};

const theme = new DesignSystem(tokens);
export type Theme = typeof theme;
export type WithTheme = { theme: Theme };

export default theme;
